/**
 * Pre-race countdown scene.
 *
 * Sits between BETTING_WINDOW close and RACE_RUNNING. Renders a big centered
 * "5..4..3..2..1..GO!" counter with brand-yellow glow, a pulse animation per
 * tick, and SFX wired from the page-level reducer (main.ts plays
 * `sfx.countdown()` per tick and `sfx.pistol()` on GO!).
 *
 * The visible label is driven by the wall clock vs `endsAtMs`, so race-start
 * SFX timing matches the on-screen "GO!" exactly. The harness can also set
 * the label directly via `setLabel(...)` for instant snapshots in tests.
 */

import type { CountdownState } from '../state/types.js';

export interface CountdownHandle {
  el: HTMLDivElement;
  setCountdown(c: CountdownState): void;
  /** Force-render a specific label (test/screenshot affordance). */
  setLabel(label: string): void;
  setVisible(v: boolean): void;
  /** Called each animation frame from main.ts. */
  tick(): void;
}

const STYLES = `
  @keyframes hr-countdown-pop {
    0%   { opacity: 0; transform: translate(-50%, -50%) scale(0.55); }
    25%  { opacity: 1; transform: translate(-50%, -50%) scale(1.18); }
    60%  { opacity: 1; transform: translate(-50%, -50%) scale(1.0);  }
    100% { opacity: 0.85; transform: translate(-50%, -50%) scale(0.96); }
  }
  @keyframes hr-countdown-glow {
    0%, 100% { text-shadow:
      0 8px 0 rgba(0,0,0,0.7),
      0 0 60px rgba(255,196,61,0.65),
      0 0 120px rgba(255,196,61,0.45);
    }
    50%      { text-shadow:
      0 8px 0 rgba(0,0,0,0.7),
      0 0 90px rgba(255,196,61,1.0),
      0 0 200px rgba(255,196,61,0.7);
    }
  }
  @keyframes hr-countdown-go-shake {
    0%, 100% { transform: translate(-50%, -50%) scale(1.4) rotate(0deg); }
    25%      { transform: translate(-50%, -52%) scale(1.46) rotate(-2deg); }
    75%      { transform: translate(-50%, -48%) scale(1.46) rotate(2deg); }
  }
  .hr-countdown-root {
    position: absolute; inset: 0;
    /* Semi-transparent vignette so the race course (horses at the gate, lanes,
       finish line) stays visible behind the big countdown digits. */
    background: radial-gradient(ellipse at center, rgba(6, 12, 233, 0.10), rgba(10, 14, 39, 0.55) 70%);
    z-index: 4;
    overflow: hidden;
  }
  .hr-countdown-label {
    position: absolute; left: 50%; top: 50%;
    transform: translate(-50%, -50%);
    font-family: 'Oswald', 'Bebas Neue', sans-serif;
    font-weight: 700;
    color: var(--sg-primary, #FFC43D);
    letter-spacing: 0.02em;
    line-height: 1;
    pointer-events: none;
    user-select: none;
    -webkit-text-stroke: 6px rgba(0,0,0,0.55);
    paint-order: stroke fill;
  }
  .hr-countdown-label.tick {
    font-size: 360px;
    animation: hr-countdown-pop 1000ms cubic-bezier(0.34, 1.56, 0.64, 1) both,
               hr-countdown-glow 1000ms ease-in-out both;
  }
  .hr-countdown-label.go {
    font-size: 280px;
    color: var(--sg-celebration, #22C55E);
    -webkit-text-stroke: 8px rgba(0,0,0,0.6);
    animation: hr-countdown-pop 600ms cubic-bezier(0.34, 1.56, 0.64, 1) both,
               hr-countdown-glow 1200ms ease-in-out both,
               hr-countdown-go-shake 360ms ease-in-out 200ms;
  }
  .hr-countdown-sub {
    position: absolute; left: 50%; top: 78%;
    transform: translateX(-50%);
    font-family: 'Bebas Neue', sans-serif;
    font-size: 56px; letter-spacing: 0.32em;
    color: var(--sg-accent, #00F0FF);
    text-shadow: 0 4px 0 rgba(0,0,0,0.6), 0 0 24px rgba(0, 240, 255, 0.4);
    text-transform: uppercase;
    pointer-events: none;
  }
  .hr-countdown-ring {
    position: absolute; left: 50%; top: 50%;
    transform: translate(-50%, -50%);
    width: 720px; height: 720px;
    border-radius: 50%;
    border: 6px solid rgba(255, 196, 61, 0.18);
    box-shadow: inset 0 0 60px rgba(255, 196, 61, 0.18),
                0 0 80px rgba(255, 196, 61, 0.12);
    pointer-events: none;
  }
`;

let stylesInjected = false;
function ensureStyles(): void {
  if (stylesInjected) return;
  const tag = document.createElement('style');
  tag.textContent = STYLES;
  document.head.appendChild(tag);
  stylesInjected = true;
}

interface RenderState {
  current: CountdownState | null;
  /** Last rendered label, so we only DOM-mutate on tick changes. */
  lastLabel: string;
}

export function createCountdown(stage: HTMLElement): CountdownHandle {
  ensureStyles();

  const el = document.createElement('div');
  el.setAttribute('data-scene', 'countdown');
  el.setAttribute('data-testid', 'hr-countdown');
  el.className = 'hr-countdown-root';
  stage.appendChild(el);

  const ring = document.createElement('div');
  ring.className = 'hr-countdown-ring';
  el.appendChild(ring);

  const label = document.createElement('div');
  label.className = 'hr-countdown-label tick';
  label.setAttribute('data-testid', 'hr-countdown-label');
  label.textContent = '';
  el.appendChild(label);

  const sub = document.createElement('div');
  sub.className = 'hr-countdown-sub';
  sub.textContent = 'GET READY';
  el.appendChild(sub);

  const state: RenderState = {
    current: null,
    lastLabel: '',
  };

  function applyLabel(next: string): void {
    if (next === state.lastLabel) return;
    state.lastLabel = next;
    // Re-trigger the CSS animation by toggling the class off then on.
    label.classList.remove('tick', 'go');
    // Force reflow so removing+adding the class restarts the animation.
    void label.offsetWidth;
    if (next === 'GO!') {
      label.classList.add('go');
      sub.textContent = "AND THEY'RE OFF!";
    } else {
      label.classList.add('tick');
      sub.textContent = 'GET READY';
    }
    label.textContent = next;
  }

  function computeLabel(now: number): string {
    if (!state.current) return '';
    const remaining = state.current.endsAtMs - now;
    if (remaining <= 0) return 'GO!';
    // Tick label = ceil(remaining / 1000), but display "GO!" once we cross
    // the final boundary. e.g. 5000ms remaining -> "5", 4999..4001 -> "5",
    // 1000..1 -> "1", 0 -> "GO!".
    const secs = Math.ceil(remaining / 1000);
    return String(Math.max(1, secs));
  }

  return {
    el,
    setCountdown(c) {
      state.current = c;
      state.lastLabel = '';
      applyLabel(computeLabel(Date.now()));
    },
    setLabel(next) {
      applyLabel(next);
    },
    setVisible(v) {
      el.style.display = v ? 'block' : 'none';
    },
    tick() {
      if (!state.current || el.style.display === 'none') return;
      applyLabel(computeLabel(Date.now()));
    },
  };
}
